/**
 * The one QueryClient (UI overhaul PR-2, audit S2).
 *
 * Every piece of server data the app shows is fetched through this client,
 * so two components asking for the same thing share one request and one
 * copy, a request whose result nobody will see is cancelled (`useApiQuery`
 * passes the query signal through `withRequestSignal`), and a pane that was
 * hidden for a while comes back with data instead of a spinner (`gcTime`).
 *
 * The defaults are deliberately quiet: nothing refetches because a window
 * regained focus or the network flapped — a page refreshes on its own
 * cadence (`poll`) or when something on it changed (`invalidatePrefix`).
 *
 * The defaults live here as named constants because TanStack merges options
 * with a plain spread: a caller that passes `staleTime: undefined` REPLACES
 * the default with undefined (= always stale). `useApiQuery` therefore
 * resolves every option it forwards through these constants and never passes
 * an undefined one (pinned by useApiQuery.test.ts).
 */
import { QueryClient } from '@tanstack/react-query'
import { isAbortError } from './requestSignal'
import { attachFailureCounter } from './failures'

/** How long a fetched answer is reused without asking the server again. */
export const STALE_MS = 30_000
/** How long an answer nobody is looking at stays cached — a tab hidden for
 *  most of an hour returns with its data (PR-1b hides panes with Activity). */
export const GC_MS = 45 * 60_000
/** One retry for a real failure; a cancelled request is not a failure. */
export const DEFAULT_RETRIES = 1

export function defaultRetry(failureCount: number, error: unknown): boolean {
  return !isAbortError(error) && failureCount < DEFAULT_RETRIES
}

export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        gcTime: GC_MS,
        retry: defaultRetry,
        retryDelay: 1_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchIntervalInBackground: false,
      },
    },
  })
  attachFailureCounter(client)
  return client
}

export const queryClient: QueryClient = createQueryClient()
